import { useNavigate } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import useSubscriptionStore from '../../stores/subscriptionStore';

// Days between the suspension and the permanent deletion of an unpaid account:
// the server suspends, deactivates 30 days later, then purges 30 days after that
// (DEACTIVATE_AFTER_SUSPENSION_DAYS + PURGE_AFTER_DEACTIVATION_DAYS in
// server/src/services/account_cleanup_service.py). Keep the two in step.
export const SUSPENSION_TO_DELETION_DAYS = 60;

// The API serialises the naive UTC `grace_period_ends_at` column without a zone
// suffix; `new Date('2026-10-15T12:00:00')` would read it as LOCAL time and move
// the shown day for anyone off UTC. Treat a suffix-less timestamp as UTC.
const HAS_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

export const parseGraceEnd = value => {
  if (!value) return null;
  const text = String(value);
  const date = new Date(HAS_ZONE.test(text) || !text.includes('T') ? text : `${text}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDay = date => date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

/**
 * PastDueBanner — the one truthful "payment failed" notice.
 *
 * States the real consequence: the account is suspended on the grace-period end
 * date and deleted SUSPENSION_TO_DELETION_DAYS days after that, unless the open
 * invoice is paid or the card is updated. Shared by GracePeriodBanner (grace still
 * running) and TrialBanner (past_due without a live grace window).
 *
 * A seller who holds a comp (`suspensionExempt`, from the server) is never suspended or
 * deleted for the failed payment, so for them the notice says the payment failed and the
 * plan is kept, with no date and no deletion threat.
 */
export const PastDueBanner = ({ gracePeriodEndsAt, suspensionExempt = false }) => {
  const navigate = useNavigate();
  const endsAt = parseGraceEnd(gracePeriodEndsAt);
  const dateLabel = endsAt ? formatDay(endsAt) : null;
  const elapsed = endsAt !== null && endsAt.getTime() <= Date.now();

  let headline = 'Payment failed — your account will be suspended soon';
  if (dateLabel) {
    headline = elapsed
      ? `Payment failed — your grace period ended on ${dateLabel} and your account is about to be suspended`
      : `Payment failed — your account will be suspended on ${dateLabel}`;
  }

  let detail = `Pay the open invoice or update your card to keep your account. Otherwise it is deleted ${SUSPENSION_TO_DELETION_DAYS} days after it is suspended.`;
  if (suspensionExempt) {
    headline = 'Payment failed — your plan is kept';
    detail = 'Your account will not be suspended. Update your card to settle the failed payment.';
  }

  return (
    <div
      role="alert"
      className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3 mb-4 flex items-center justify-between gap-4"
      data-testid="grace-period-banner"
    >
      <div className="flex items-center gap-3">
        <ExclamationTriangleIcon className="w-5 h-5 shrink-0 text-amber-800 dark:text-amber-200" aria-hidden="true" />
        <div>
          <p className="text-sm font-medium text-amber-800 dark:text-amber-200">{headline}</p>
          <p className="text-xs text-amber-900 dark:text-amber-100">{detail}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={() => navigate('/subscription')}
        className="shrink-0 px-4 py-1.5 bg-amber-700 text-white text-sm font-medium rounded-md hover:bg-amber-800 transition-colors"
      >
        Update Payment
      </button>
    </div>
  );
};

/**
 * GracePeriodBanner — shown when a user's payment has failed but they are
 * still within the grace period.
 *
 * During grace the user keeps access to their paid tier (see
 * `get_effective_tier` on the backend). The banner says when the account is
 * suspended and that it is deleted 60 days after that unless the open invoice
 * is paid or the card updated, and sends them to /subscription to fix it.
 *
 * Returns null when the user is not in a grace period.
 */
const GracePeriodBanner = () => {
  const isInGracePeriod = useSubscriptionStore(state => state.isInGracePeriod);
  const gracePeriodEndsAt = useSubscriptionStore(state => state.gracePeriodEndsAt);
  const suspensionExempt = useSubscriptionStore(state => state.suspensionExempt);

  if (!isInGracePeriod) {
    return null;
  }

  return <PastDueBanner gracePeriodEndsAt={gracePeriodEndsAt} suspensionExempt={suspensionExempt} />;
};

export default GracePeriodBanner;
